/**
 * Handler pour les messages de scénario
 * Gère le parsing, le routage et la mise à jour de l'état
 */

import {
	ScenarioMessage,
	ScenarioProgressMessage,
	ScenarioResultMessage,
	ScenarioCompleteMessage,
	ScenarioFlowState,
	ScenarioMessageCallbacks,
	ProgressStep
} from './types';

// Mapping des étapes vers le pourcentage de progression
const STEP_PROGRESS: Record<ProgressStep, number> = {
	started: 5,
	fetching_project: 10,
	deep_research: 20,
	ai_analysis: 40,
	reformatting: 55,
	processing_stakeholders: 60,
	processing_stakes: 65,
	processing_risks: 70,
	processing_constraints: 75,
	scenarios_started: 78,
	scenario_progress: 85,
	completed: 100,
	error: 0
};

/**
 * Parse un message brut JSON en ScenarioMessage typé
 */
export function parseScenarioMessage(
	rawMessage: string
): ScenarioMessage | null {
	try {
		const parsed = JSON.parse(rawMessage);

		// Vérifier que c'est un message de scénario
		if (
			parsed.type === 'scenario_progress' ||
			parsed.type === 'scenario_result' ||
			parsed.type === 'scenario_complete'
		) {
			return parsed as ScenarioMessage;
		}

		return null;
	} catch (error) {
		console.error('Error parsing scenario message:', error);
		return null;
	}
}

/**
 * Crée un état initial pour le flux de scénario
 */
export function createInitialState(): ScenarioFlowState {
	return {
		isRunning: false,
		currentStep: null,
		progress: 0,
		error: null,
		query: null,
		news: null,
		scenarioInfo: {
			name: null,
			description: null,
			analysis: null
		},
		results: {
			stakeholders: null,
			stakes: null,
			risks: null,
			indicators: null
		},
		prospectiveScenarios: [],
		prospectiveStatuses: [],
		finalResult: null
	};
}

/**
 * Gère un message de progression
 */
function handleProgressMessage(
	state: ScenarioFlowState,
	message: ScenarioProgressMessage,
	callbacks?: ScenarioMessageCallbacks
): ScenarioFlowState {
	const newState = { ...state };

	newState.currentStep = message.step;
	newState.progress = STEP_PROGRESS[message.step] || 0;

	if (message.step === 'started') {
		newState.isRunning = true;
		newState.query = message.query || null;
	} else if (message.step === 'scenarios_started') {
		const count = message.scenarioCount || 3;
		newState.prospectiveScenarios = [];
		newState.prospectiveStatuses = Array(count).fill('pending');
	} else if (message.step === 'scenario_progress' && message.scenarioIndex != null) {
		const statuses = [...newState.prospectiveStatuses];
		statuses[message.scenarioIndex] = message.status || 'started';
		newState.prospectiveStatuses = statuses;
		if (message.status === 'completed' && message.scenario) {
			const scenarios = [...newState.prospectiveScenarios];
			scenarios[message.scenarioIndex] = message.scenario;
			newState.prospectiveScenarios = scenarios;
		}
	} else if (message.step === 'completed') {
		newState.isRunning = false;
	} else if (message.step === 'error') {
		newState.isRunning = false;
		newState.error = message.error || 'Unknown error';
		callbacks?.onError?.(newState.error);
	}

	callbacks?.onProgress?.(message.step, message.message);

	return newState;
}

/**
 * Gère un message de résultat
 */
function handleResultMessage(
	state: ScenarioFlowState,
	message: ScenarioResultMessage,
	callbacks?: ScenarioMessageCallbacks
): ScenarioFlowState {
	const newState = { ...state };

	switch (message.objectType) {
		case 'news':
			newState.news = message.content || null;
			callbacks?.onNewsReceived?.(message.content || '');
			break;

		case 'scenario_info':
			newState.scenarioInfo = {
				name: message.scenarioName || null,
				description: message.scenarioDescription || null,
				analysis: message.analysis || null
			};
			if (
				message.scenarioName &&
				message.scenarioDescription &&
				message.analysis
			) {
				callbacks?.onScenarioInfo?.({
					name: message.scenarioName,
					description: message.scenarioDescription,
					analysis: message.analysis
				});
			}
			break;

		case 'stakeholders':
			newState.results = {
				...newState.results,
				stakeholders: {
					modifications: message.modifications || [],
					newElements: message.newElements || []
				}
			};
			callbacks?.onObjectResult?.(
				'stakeholders',
				message.modifications || [],
				message.newElements || []
			);
			break;

		case 'stakes':
			newState.results = {
				...newState.results,
				stakes: {
					modifications: message.modifications || [],
					newElements: message.newElements || []
				}
			};
			callbacks?.onObjectResult?.(
				'stakes',
				message.modifications || [],
				message.newElements || []
			);
			break;

		case 'risks':
			newState.results = {
				...newState.results,
				risks: {
					modifications: message.modifications || [],
					newElements: message.newElements || []
				}
			};
			callbacks?.onObjectResult?.(
				'risks',
				message.modifications || [],
				message.newElements || []
			);
			break;

		case 'indicators':
			newState.results = {
				...newState.results,
				indicators: {
					modifications: message.modifications || [],
					newElements: message.newElements || []
				}
			};
			callbacks?.onObjectResult?.(
				'indicators',
				message.modifications || [],
				message.newElements || []
			);
			break;
	}

	return newState;
}

/**
 * Gère un message de complétion
 */
function handleCompleteMessage(
	state: ScenarioFlowState,
	message: ScenarioCompleteMessage,
	callbacks?: ScenarioMessageCallbacks
): ScenarioFlowState {
	const newState = { ...state };

	newState.isRunning = false;
	newState.currentStep = 'completed';
	newState.progress = 100;
	newState.finalResult = message.result;

	callbacks?.onComplete?.(message.result);

	return newState;
}

/**
 * Reducer principal pour traiter un message
 * Peut être utilisé directement dans un useReducer React
 */
export function scenarioMessageReducer(
	state: ScenarioFlowState,
	message: ScenarioMessage,
	callbacks?: ScenarioMessageCallbacks
): ScenarioFlowState {
	switch (message.type) {
		case 'scenario_progress':
			return handleProgressMessage(state, message, callbacks);
		case 'scenario_result':
			return handleResultMessage(state, message, callbacks);
		case 'scenario_complete':
			return handleCompleteMessage(state, message, callbacks);
		default:
			return state;
	}
}

/**
 * Classe handler pour usage impératif (utile pour les tests)
 */
export class ScenarioMessageHandler {
	private state: ScenarioFlowState;
	private callbacks: ScenarioMessageCallbacks;

	constructor(callbacks: ScenarioMessageCallbacks = {}) {
		this.state = createInitialState();
		this.callbacks = callbacks;
	}

	/**
	 * Traite un message brut
	 */
	handleRawMessage(rawMessage: string): ScenarioFlowState {
		const message = parseScenarioMessage(rawMessage);
		if (message) {
			this.state = scenarioMessageReducer(
				this.state,
				message,
				this.callbacks
			);
		}
		return this.state;
	}

	/**
	 * Traite un message déjà parsé
	 */
	handleMessage(message: ScenarioMessage): ScenarioFlowState {
		this.state = scenarioMessageReducer(
			this.state,
			message,
			this.callbacks
		);
		return this.state;
	}

	/**
	 * Récupère l'état actuel
	 */
	getState(): ScenarioFlowState {
		return this.state;
	}

	/**
	 * Réinitialise l'état
	 */
	reset(): void {
		this.state = createInitialState();
	}

	/**
	 * Met à jour les callbacks
	 */
	setCallbacks(callbacks: ScenarioMessageCallbacks): void {
		this.callbacks = callbacks;
	}
}
